import React from 'react';
import { capitalize } from 'lodash';
import cx from 'classnames';
import { pluralize } from 'utilities/pluralize';
import ContextualLink from '@magoosh/gre/app/contextual_link';
import StatusMessage from '@magoosh/gre/adaptive_prep_v2/lib/status_message';
import { NoteProps } from './types';
import style from './style.module.scss';
import { paths } from 'config/path_helpers';
import { useQueries } from '@magoosh/gre/app/queries';

interface Props {
  sectionName: string;
};

const ExamSectionNotes: React.FC<Props> = (props: Props) => {
  const { sectionName } = props;
  const query = useQueries().notesQuery(sectionName);

  if (query.isLoading) {
    return (
      <StatusMessage
        headline="One Second!"
        isColored
        text={`We are looking for your ${sectionName} notes`}
      />
    );
  } else {
    const {
      lessonNotes,
      questionNotes
    } = query.data;

    return (
      <>
        <h3 className="u-margin-B-n">{capitalize(sectionName)} Notes</h3>
        <small>{pluralize(lessonNotes.length + questionNotes.length, 'Note', 'Notes')}</small>

        <div className={style.notes}>
          <div className={cx(style.notesColumn)}>
            <h4 className="u-margin-B-n">Lessons</h4>
            {lessonNotes.length === 0 ? (
              <>
                <p>You have not taken any notes on {sectionName} lessons.</p>
                <ContextualLink href={paths.dashboard()}>Go to Study Plan</ContextualLink>
              </>
            ) : (
              <>
                <p>
                  <small>{pluralize(lessonNotes.length, 'Note', 'Notes')}</small>
                </p>
                {lessonNotes.map(renderNote)}
              </>
            )}
          </div>
          <div className={cx(style.notesColumn)}>
            <h4 className="u-margin-B-n">Questions</h4>
            {questionNotes.length === 0 ? (
              <>
                <p>You have not taken any notes on {sectionName} questions.</p>
                <ContextualLink href={paths.answers()}>Go to Review</ContextualLink>
              </>
            ) : (
              <>
                <p>
                  <small>{pluralize(questionNotes.length, 'Note', 'Notes')}</small>
                </p>
                {questionNotes.map(renderNote)}
              </>
            )}
          </div>
        </div>
      </>
    );
  }
};

const renderNote = (note: NoteProps) => {
  return (
    <div className={cx(style.note, 'u-padding-V-s')} key={note.id}>
      <p>{note.text}</p>
      <p className="u-margin-B-n">
        <small>
          {note.link ? (
            <>
              <ContextualLink href={note.link}>{note.linkText}</ContextualLink> on {note.timestamp}
            </>
          ) : (
            <>
              <span>{note.linkText}</span> on {note.timestamp}
            </>
          )}
        </small>
      </p>
    </div>
  );
};

export default ExamSectionNotes;
